import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BarChart3, Heart, Bot, Download, Loader2, Globe, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react'
import { DailyHealthDashboard } from '@/components/monitoring/tabs/DailyHealthDashboard'
import { SystemHealth } from '@/components/monitoring/tabs/SystemHealth'
import { OptimizerMonitor } from '@/components/monitoring/tabs/OptimizerMonitor'
import { monitoringService, LegacyGatewayHealth } from '@/services/monitoring'

/**
 * Health Dashboard - System Health Page
 *
 * Admin-only page for monitoring system health and daily metrics.
 *
 * SECURITY: Only accessible to users with admin role.
 */
export function HealthPage() {
  const { hasRole } = useRoleAccess()
  const [activeTab, setActiveTab] = useState('daily')
  const [downloadingAudit, setDownloadingAudit] = useState(false)

  // Legacy Gateway Health state
  const [legacyHealth, setLegacyHealth] = useState<LegacyGatewayHealth | null>(null)
  const [legacyHealthLoading, setLegacyHealthLoading] = useState(false)
  const [legacyHealthError, setLegacyHealthError] = useState<string | null>(null)

  // Admin role guard - redirect non-admin users
  if (!hasRole('admin')) {
    return <Navigate to="/" replace />
  }

  // Fetch legacy gateway health when API tab is active
  const fetchLegacyHealth = async () => {
    setLegacyHealthLoading(true)
    setLegacyHealthError(null)
    try {
      const health = await monitoringService.getLegacyGatewayHealth()
      setLegacyHealth(health)
    } catch (err: any) {
      console.error('Failed to fetch legacy gateway health:', err)
      if (err.response?.status === 404) {
        setLegacyHealthError('Endpoint not deployed yet. Deploy the API with /v2/data/health endpoint.')
      } else {
        setLegacyHealthError(err.message || 'Failed to fetch health status')
      }
    } finally {
      setLegacyHealthLoading(false)
    }
  }

  // Load legacy health when switching to API tab
  useEffect(() => {
    if (activeTab === 'api') {
      fetchLegacyHealth()
    }
  }, [activeTab])

  const handleDownloadAuditCsv = async () => {
    setDownloadingAudit(true)
    try {
      await monitoringService.downloadAuditCsv()
    } catch (error) {
      console.error('Failed to download audit CSV:', error)
    } finally {
      setDownloadingAudit(false)
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            Health Dashboard
          </h1>
          <p className="text-muted-foreground">
            System health metrics and daily performance indicators.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleDownloadAuditCsv}
          disabled={downloadingAudit}
        >
          {downloadingAudit ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download Audit CSV
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="daily" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span>Daily Health</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Heart className="h-4 w-4" />
            <span>System</span>
          </TabsTrigger>
          <TabsTrigger value="optimizer" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            <span>Optimizer</span>
          </TabsTrigger>
          <TabsTrigger value="api" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <span>API</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Daily Health Dashboard */}
        <TabsContent value="daily" className="space-y-4">
          <DailyHealthDashboard />
        </TabsContent>

        {/* Tab 2: System Health */}
        <TabsContent value="system" className="space-y-4">
          <SystemHealth />
        </TabsContent>

        {/* Tab 3: Optimizer Monitor */}
        <TabsContent value="optimizer" className="space-y-4">
          <OptimizerMonitor />
        </TabsContent>

        {/* Tab 4: API Gateway Health */}
        <TabsContent value="api" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-xl">Legacy Gateway Status</CardTitle>
                <CardDescription>Health status of the /v2/data API gateway</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchLegacyHealth}
                disabled={legacyHealthLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${legacyHealthLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {legacyHealthLoading && !legacyHealth ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : legacyHealthError ? (
                <div className="flex items-center gap-2 text-destructive py-4">
                  <XCircle className="h-5 w-5" />
                  <span>{legacyHealthError}</span>
                </div>
              ) : legacyHealth ? (
                <div className="space-y-6">
                  {/* Status Badge */}
                  <div className="flex items-center gap-3">
                    {legacyHealth.status === 'healthy' && (
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <CheckCircle className="h-6 w-6" />
                        <span className="text-lg font-semibold">Healthy</span>
                      </div>
                    )}
                    {legacyHealth.status === 'warning' && (
                      <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                        <AlertTriangle className="h-6 w-6" />
                        <span className="text-lg font-semibold">Warning</span>
                      </div>
                    )}
                    {legacyHealth.status === 'critical' && (
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                        <XCircle className="h-6 w-6" />
                        <span className="text-lg font-semibold">Critical</span>
                      </div>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Last 1 Hour */}
                    <div className="p-4 rounded-lg border bg-muted/30">
                      <div className="text-sm text-muted-foreground mb-2">Last 1 Hour</div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="font-mono text-lg">{legacyHealth.completed_1h}</span>
                          <span className="text-sm text-muted-foreground">completed</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="font-mono text-lg">{legacyHealth.failed_1h}</span>
                          <span className="text-sm text-muted-foreground">failed</span>
                        </div>
                      </div>
                    </div>

                    {/* Last 24 Hours */}
                    <div className="p-4 rounded-lg border bg-muted/30">
                      <div className="text-sm text-muted-foreground mb-2">Last 24 Hours</div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="font-mono text-lg">{legacyHealth.completed_24h}</span>
                          <span className="text-sm text-muted-foreground">completed</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="font-mono text-lg">{legacyHealth.failed_24h}</span>
                          <span className="text-sm text-muted-foreground">failed</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-orange-500" />
                      <span className="font-mono">{legacyHealth.stuck_jobs}</span>
                      <span className="text-muted-foreground">stuck jobs</span>
                    </div>
                    <div className="text-muted-foreground">
                      Total (24h): <span className="font-mono">{legacyHealth.total_24h}</span> jobs
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground py-4">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
